import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavigation } from './_NavigationContext';
import LocaleService from './services/LocaleService';

const LangContext = createContext();

export const LangProvider = ({ children }) =>
{
	const location = useLocation();
	const { navigateWithParams, navigateSetParam } = useNavigation();

	const queryParams = new URLSearchParams(window.location.search);
	const urlLang = queryParams.get('lang');
	
	const [lang, setLang] = useState(urlLang || 'ja');
	const [isLocaleLoading, setIsLocaleLoading] = useState(true);
	const [isLocaleReady, setIsLocaleReady] = useState(false);

	// Listen for URL parameter changes and update language
	useEffect(() =>
	{
		const params = new URLSearchParams(location.search);
		const newLang = params.get('lang') || 'ja';
		
		if (newLang !== lang)
		{
			setLang(newLang);
		}
	},
	[location.search, lang]);

	// Preload essential locales when language changes
	useEffect(() =>
	{
		const preloadLocales = async () =>
		{
			setIsLocaleLoading(true);
			setIsLocaleReady(false);
			try 
			{
				await LocaleService.preloadEssentialLocales(lang);
				setIsLocaleReady(true);
			} 
			catch (error) 
			{
				console.error('Failed to preload locales:', error);
			} 
			finally 
			{
				setIsLocaleLoading(false);
			}
		};

		preloadLocales();
	},
	[lang]);

	const changeLang = useCallback((newLang) =>
	{
        if (newLang !== lang)
		{
            navigateSetParam('lang', newLang, 'ja');
            setLang(newLang);
        }
    },
	[lang, navigateSetParam]);

	/**
	 * Get localized text with fallback support (async)
	 */
	const getLocalizedText = useCallback(async (key, fallback = '') =>
	{
		return await LocaleService.getLocalizedText(lang, key, fallback);
	},
	[lang]);

	/**
	 * Generic locale getter - can access any top-level key (terms, messages, etc.)
	 */
	const getLocale = useCallback((key, parentKey = 'terms', fallback = '') =>
	{
		return LocaleService.getLocale(lang, key, parentKey, fallback);
	},
	[lang]);

	/**
	 * Get text from 'terms' section
	 */
	const getTerm = useCallback((key, fallback = '') =>
	{
		return LocaleService.getTerm(lang, key, fallback);
	},
	[lang]);

	/**
	 * Get text from 'messages' section
	 */
	const getMessage = useCallback((key, fallback = '') =>
	{
		return LocaleService.getMessage(lang, key, fallback);
	},
	[lang]);

	/**
	 * Get localized text synchronously (for already loaded data)
	 * This is the main method components should use
	 * @deprecated Use getTerm() instead
	 */
	const getLocalizedTextSync = useCallback((key, fallback = '') =>
	{
		return LocaleService.getTerm(lang, key, fallback);
	},
	[lang]);

	/**
	 * Alias for getLocalizedTextSync for easier migration
	 * @deprecated Use getTerm() instead
	 */
	const getText = useCallback((key, fallback = '') =>
	{
		return LocaleService.getTerm(lang, key, fallback);
	},
	[lang]);

	/**
	 * Get localized data for buildings, exteriors, etc. (sync)
	 */
	const getItemData = useCallback((dataType, id) =>
	{
		return LocaleService.getItemData(lang, dataType, id);
	},
	[lang]);

	/**
	 * Get all items of a specific type (sync)
	 */
	const getAllItems = useCallback((dataType) =>
	{
		return LocaleService.getAllItems(lang, dataType);
	},
	[lang]);

	/**
	 * Get localized data for buildings, exteriors, etc. (async)
	 */
	const getLocalizedData = useCallback(async (dataType, id) =>
	{
		return await LocaleService.getLocalizedData(lang, dataType, id);
	},
	[lang]);

	/**
	 * Check if voiceover is enabled for current language
	 */
	const isVoiceoverEnabled = useCallback(() =>
	{
		return LocaleService.isVoiceoverEnabled(lang);
	},
	[lang]);

	/**
	 * Get available languages (only enabled ones)
	 */
	const getAvailableLanguages = useCallback(() =>
	{
		return LocaleService.getAvailableLanguages();
	},
	[]);

	return (
		<LangContext.Provider value={{ 
			lang, 
			changeLang,
			isLocaleLoading,
			isLocaleReady,
			getLocale,
			getTerm,
			getMessage,
			getLocalizedText,
			getLocalizedTextSync,
			getText,
			getItemData,
			getAllItems,
			getLocalizedData,
			isVoiceoverEnabled,
			getAvailableLanguages
		}}>
			{children}
		</LangContext.Provider>
	);
};

export const useLang = () =>
{
	const context = useContext(LangContext);
	if (!context)
	{
		throw new Error('useLang must be used within a LangProvider');
	}
	return context;
};